import { FormikErrors, FormikTouched } from "formik";
import { TETexts } from "../../components/tilsynserklaering/texts";
import {
  IEjerandelItem,
  TilsynserklaeringFormValues,
} from "../../interfaces/tilsynserklaering";
import { hasTrueValueWithPrefix } from "../common";
import { TOUCHED_EJERANDELITEM } from "../../constants/initialValues";
import { BooleanValuePairs } from "../../interfaces/common";

export type TEIsCompleteHelpers = ReturnType<typeof TEIsComplete>;

const getSectionComplete = (
  textSection: object,
  errors: FormikErrors<TilsynserklaeringFormValues>,
) =>
  Object.keys(textSection).reduce((acc, key) => {
    if (errors[key] !== undefined) {
      return false;
    }
    return acc;
  }, true);

const getSectionTouched = (
  section: object,
  values?: TilsynserklaeringFormValues,
) => {
  const iterateObject = (obj: object, acc: Record<string, boolean> = {}) => {
    Object.keys(obj).forEach((key) => {
      if (obj[key] && typeof obj[key] === "object") {
        iterateObject(obj[key], acc);
      } else if (key === "id" && typeof obj[key] === "string") {
        acc[obj[key]] = true;
      }
    });
    return acc;
  };
  const touched = iterateObject(section);
  if (
    touched.tilsynEjerandele &&
    values?.tilsynEjerandele &&
    values.tilsynEjerandele.length > 0
  ) {
    const tilsynEjerandele: BooleanValuePairs<IEjerandelItem>[] =
      values.tilsynEjerandele.map(() => TOUCHED_EJERANDELITEM);
    return { ...touched, tilsynEjerandele };
  }
  return touched;
};

/**
 * @deprecated DO NOT USE!! returns incorrect values
 */
export const TEIsComplete = (
  texts: TETexts,
  values: TilsynserklaeringFormValues,
  errors: FormikErrors<TilsynserklaeringFormValues>,
) => {
  const section_1_touched = getSectionTouched(texts.section1);
  const section_1_complete = getSectionComplete(texts.section1, errors);
  const section_2_touched = getSectionTouched(texts.section2, values);
  const section_2_complete = getSectionComplete(texts.section2, errors);
  const section_3_touched = getSectionTouched(texts.section3);
  const section_3_complete = getSectionComplete(texts.section3, errors);
  const section_4_touched = getSectionTouched(texts.section4);
  const section_4_complete = getSectionComplete(texts.section4, errors);
  const hasSagstype = hasTrueValueWithPrefix(values, "sagstype");
  const hasFormaal =
    values.tilsynEjerandele?.length > 0
      ? !values.tilsynEjerandele.some(
          (ejerandel) => !hasTrueValueWithPrefix(ejerandel, "formaal"),
        )
      : true;

  const allComplete =
    section_1_complete &&
    section_2_complete &&
    section_3_complete &&
    section_4_complete &&
    hasSagstype &&
    hasFormaal;

  return {
    section_1_complete,
    section_2_complete,
    section_3_complete,
    section_4_complete,
    section_1_touched,
    section_2_touched,
    section_3_touched,
    section_4_touched,
    allComplete,
  };
};

export const tilsynSection1CompleteHelper = (
  values: TilsynserklaeringFormValues,
  errors: FormikErrors<TilsynserklaeringFormValues>,
  touched: FormikTouched<TilsynserklaeringFormValues>,
) => {
  const errorFields = Object.keys(errors);
  const touchedFields = Object.keys(touched);
  const fieldKeys: Array<keyof TilsynserklaeringFormValues> = [
    "forsikringSidstAar",
    "nuvaerendeForsikringsselskab",
    "nuvaerendeForsikringPolicenr",
  ];

  // check if section has been started
  const hasAnyTouched = fieldKeys.some((key) => touchedFields.includes(key));
  const hasAnyValue = fieldKeys.some((key) => values[key]);
  const isSectionStarted = hasAnyTouched || hasAnyValue;

  // check if section is complete
  const isSectionComplete = fieldKeys.every(
    (key) => !errorFields.includes(key),
  );

  return {
    section1IsStarted: isSectionStarted,
    section1IsComplete: isSectionComplete,
  };
};

export const tilsynSection2CompleteHelper = (
  values: TilsynserklaeringFormValues,
  errors: FormikErrors<TilsynserklaeringFormValues>,
  touched: FormikTouched<TilsynserklaeringFormValues>,
) => {
  const errorFields = Object.keys(errors);
  const touchedFields = Object.keys(touched);
  const fieldKeys: Array<keyof TilsynserklaeringFormValues> =
    values["ejerandeleIAndenVirksomhed"] === "ja"
      ? ["ejerandeleIAndenVirksomhed", "tilsynEjerandele"]
      : ["ejerandeleIAndenVirksomhed"];

  // check if section has been started
  const hasAnyTouched = fieldKeys.some((key) => touchedFields.includes(key));
  const hasAnyValue = fieldKeys.some((key) => values[key]);
  const isSectionStarted = hasAnyTouched || hasAnyValue;

  // check if section is complete
  const isSectionComplete = fieldKeys.every(
    (key) => !errorFields.includes(key),
  );

  return {
    section2IsStarted: isSectionStarted,
    section2IsComplete: isSectionComplete,
  };
};

export const tilsynSection3CompleteHelper = (
  values: TilsynserklaeringFormValues,
  errors: FormikErrors<TilsynserklaeringFormValues>,
  touched: FormikTouched<TilsynserklaeringFormValues>,
) => {
  const errorFields = Object.keys(errors);
  const touchedFields = Object.keys(touched);
  const fieldKeys: Array<keyof TilsynserklaeringFormValues> = [
    "harDuHaftForbrugersager",
    "harDuHaftForbrugersagerUddyb",
    "harDuHaftStraffesager",
  ];

  // check if section has been started
  const hasAnyTouched = fieldKeys.some((key) => touchedFields.includes(key));
  const hasAnyValue = fieldKeys.some((key) => values[key]);
  const isSectionStarted = hasAnyTouched || hasAnyValue;

  // check if section is complete
  const isSectionComplete = fieldKeys.every(
    (key) => !errorFields.includes(key),
  );

  return {
    section3IsStarted: isSectionStarted,
    section3IsComplete: isSectionComplete,
  };
};

export const tilsynSection4CompleteHelper = (
  values: TilsynserklaeringFormValues,
  errors: FormikErrors<TilsynserklaeringFormValues>,
  touched: FormikTouched<TilsynserklaeringFormValues>,
) => {
  const errorFields = Object.keys(errors);
  const touchedFields = Object.keys(touched);
  const fieldKeys: Array<keyof TilsynserklaeringFormValues> = [
    "harDuHaftHvidvasksager",
  ];

  if (values.harDuHaftHvidvasksager === "ja") {
    fieldKeys.push(
      "hvorMangeSagerOmfattetAfHvidvaskloven",
      "sagerKlientPolitiskEksponeret",
      "sagerKompleksEllerUsaedvanlig",
      "sagerSkaerpedeKundekendskabsprocedurer",
      "sagerLempedeKundekendskabsprocedurer",
      "sagerUdenforEU",
      "sagerTransaktionHoejrisikotredjelande",
      "sagerPaalagtFinansielSanktion",
      "harDuBenyttetTredjemandEllerOutsourcingKundekendskabsprocedurer",
      "harDuUnderrettetMistankeHvidvask",
      "harDuUnderrettetMistankeFinansieringTerrorisme",
      "virksomhedRisikovurderingAdvokatvirksomhed",

      // !!! BEWARE !!!
      "hvilkeSagstyperHvidvaskloven" as keyof TilsynserklaeringFormValues,
      // VERY intentional type cast here !!!
      // since the prop is NOT in the declared typed
      // but it ONLY exists in the Yup validation schema
    );

    if (values.virksomhedRisikovurderingAdvokatvirksomhed === "ja") {
      fieldKeys.push(
        "risikovurderingVirksomhedensForretningsmodel",
        "advokatvirksomhedPolitikHvidvaskloven",
        "advokatvirksomhedForretningsgangeHvidvaskloven",
      );

      if (values.advokatvirksomhedPolitikHvidvaskloven === "ja") {
        fieldKeys.push("virksomhedenForretningsmodelSagsomraaderPaatageSig");
      }
    } else if (values.virksomhedRisikovurderingAdvokatvirksomhed === "nej") {
      fieldKeys.push(
        "advokatvirksomhedPolitikHvidvaskloven",
        "advokatvirksomhedForretningsgangeHvidvaskloven",
      );

      if (values.advokatvirksomhedPolitikHvidvaskloven === "ja") {
        fieldKeys.push("virksomhedenForretningsmodelSagsomraaderPaatageSig");
      }
    }
  }

  // check if section has been started
  const hasAnyTouched = fieldKeys.some((key) => touchedFields.includes(key));
  const hasAnyValue = fieldKeys.some((key) => values[key]);
  const isSectionStarted = hasAnyTouched || hasAnyValue;

  // check if section is complete
  const isSectionComplete = fieldKeys.every(
    (key) => !errorFields.includes(key),
  );

  return {
    section4IsStarted: isSectionStarted,
    section4IsComplete: isSectionComplete,
  };
};
